import { useEffect, useState } from "react";
import FileUpload from "../../../components/common/FileUpload";
import CustomButton from "../../../components/CustomButton";
import CustomInput from "../../../components/CustomInput";
import CustomSelect from "../../../components/CustomSelect";
import SubHeadings from "../../../components/SubHeadings";
import { Staff } from "../../../types/staff";
import { useFormEdit } from "../../../util/hooks/useFormEdit";
import StaffApis from "../../../api/staffApis";
import { formatRoleForDisplay } from "../../../util/functions/UniversalFunctions";
import { toast } from "sonner";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import UpdatePassword from "../../../components/modal/UpdatePassword";
import SubButton from "../../../components/SubButtons";
import { BiLockAlt } from "react-icons/bi";

interface PersonalInformationProps {
  staff?: Staff;
  onChange: (data: Partial<Staff>) => void;
}

export default function PersonalInformation({
  staff,
  onChange,
}: PersonalInformationProps) {
  const { isEditing } = useFormEdit<Staff>(staff || ({} as Staff));
  const [logoChanged, setLogoChanged] = useState(false);
  const [profileUrl, setProfileUrl] = useState(staff?.profile_image_url ?? "");

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const user = useSelector((state: RootState) => state.access.staff);

  const isAdmin =
    user?.roleName === "ROLE_ADMIN" || user?.roleName === "ROLE_SUPER_ADMIN";

  const handleInputChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = event.target;
    onChange({ [name]: value });
  };

  const [roles, setRoles] = useState<string[]>([]);

  useEffect(() => {
    const fetchRoles = async () => {
      const response = await StaffApis.getStaffRoles();
      if (response?.data) {
        setRoles(response.data);
      }
    };

    fetchRoles();
  }, []);

  const handleLogoUpload = (url: string) => {
    const cleanUrl = url.replace(/^"+|"+$/g, "");
    setProfileUrl(cleanUrl);
    setLogoChanged(true);
    onChange({ profile_image_url: cleanUrl });
  };

  const handleSaveLogo = async () => {
    if (!staff?.staffId || !profileUrl) return;

    try {
      await StaffApis.changeStaffProfile(staff.staffId, profileUrl);
      toast.success("Profile updated successfully");
      setLogoChanged(false);
    } catch {
      toast.error("Failed to update Profile");
    }
  };

  return (
    <div className="px-4 md:px-9 flex flex-col-reverse md:flex-row md:divide-x divide-neutral-50 gap-9">
      <div className="flex flex-col gap-7 w-full md:py-7">
        <SubHeadings text="Profile Information" />
        <form className="grid gap-7">
          <div className="grid lg:grid-cols-2 gap-7">
            <CustomInput
              name="fullName"
              label="Staff Name"
              readOnly={!isEditing}
              type="text"
              placeholder="Enter staff name"
              value={staff?.fullName || ""}
              onChange={handleInputChange}
              inputClassName="w-full"
            />

            <CustomSelect
              name="roleName"
              label="Designation"
              readOnly={true}
              value={staff?.roleName || ""}
              options={roles.map((role) => ({
                label: formatRoleForDisplay(role),
                value: role,
              }))}
              placeholder="Select staff's designation"
              onChange={handleInputChange}
            />

            <CustomInput
              name="employeeCode"
              label="Employee Code"
              type="text"
              readOnly={!isEditing}
              placeholder="Enter employee code"
              value={staff?.employeeCode || ""}
              onChange={handleInputChange}
              inputClassName="w-full"
            />

            <CustomInput
              name="email"
              label="Email Address"
              type="email"
              readOnly={!isEditing}
              placeholder="Enter email address"
              value={staff?.email || ""}
              onChange={handleInputChange}
              inputClassName="w-full"
            />

            <CustomInput
              name="mobile"
              label="Phone Number"
              type="tel"
              readOnly={!isEditing}
              placeholder="Enter phone number"
              value={staff?.mobile || ""}
              onChange={handleInputChange}
              inputClassName="w-full"
            />

            <CustomInput
              name="alternatePhoneNumber"
              label="Alt. Phone Number"
              type="tel"
              readOnly={!isEditing}
              placeholder="Enter alternate phone number"
              value={staff?.alternatePhoneNumber || ""}
              onChange={handleInputChange}
              inputClassName="w-full"
            />

            <CustomInput
              name="dateOfBirth"
              label="Date of Birth"
              type="date"
              readOnly={!isEditing}
              placeholder="DD/MM/YYYY"
              value={staff?.dateOfBirth || ""}
              onChange={handleInputChange}
              inputClassName="w-full"
            />

            <CustomInput
              name="dateOfJoining"
              label="Date of Joining"
              type="date"
              readOnly={!isEditing}
              placeholder="DD/MM/YYYY"
              value={staff?.dateOfJoining || ""}
              onChange={handleInputChange}
              inputClassName="w-full"
            />
          </div>

          {/* Gender & Submit */}
          <div className="flex flex-col md:flex-row justify-between gap-4 md:items-center w-full">
            <div className="flex gap-7">
              <label className="text-sm font-semibold text-neutral-400 block">
                Gender
              </label>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <CustomInput
                    name="gender"
                    type="radio"
                    checked={staff?.gender === "MALE"}
                    value="MALE"
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    inputClassName="w-3 h-3"
                  />
                  <span
                    className={`text-sm font-semibold ${
                      staff?.gender === "MALE"
                        ? "text-primary-500"
                        : "text-neutral-300"
                    }`}
                  >
                    Male
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <CustomInput
                    name="gender"
                    type="radio"
                    checked={staff?.gender === "FEMALE"}
                    value="FEMALE"
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    inputClassName="w-3 h-3"
                  />
                  <span
                    className={`text-sm font-semibold ${
                      staff?.gender === "FEMALE"
                        ? "text-primary-500"
                        : "text-neutral-300"
                    }`}
                  >
                    Female
                  </span>
                </div>
              </div>
            </div>

            {/* <div className="flex justify-end items-end relative group">
              <CustomButton
                color="bg-primary-500"
                text="Submit"
                disabled={!hasUnsavedChanges || !isEditing}
              />

              {!hasUnsavedChanges && (
                <span className="absolute bottom-full mb-2 left-1/2 md:-translate-x-1/2 bg-neutral-300 text-white text-xs font-medium px-3 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                  {isEditing
                    ? "No changes yet — fill out or edit the form to get started"
                    : "Enable edit mode to make changes"}
                </span>
              )}
            </div> */}
          </div>
        </form>
      </div>

      <div className="md:pl-7 pt-7 md:py-7">
        <FileUpload
          value={profileUrl}
          onUploadComplete={handleLogoUpload}
          className="md:w-52 w-36 aspect-square"
          disabled={!isEditing}
          showButton={!logoChanged}
          buttonText="Upload Image"
          uploadText="Change Image"
        />

        {isEditing && logoChanged && (
          <button
            type="button"
            onClick={handleSaveLogo}
            className="bg-primary-500 text-white rounded-lg py-2 px-4 w-full text-sm font-semibold mt-3"
          >
            Save Image
          </button>
        )}

        {isAdmin && (
          <div className="mt-5 p-4 rounded-2xl bg-background-100 border border-background-300 shadow-sm">
            <div className="flex flex-col items-center justify-between gap-3">
              {/* Left Side */}
              <div className="flex flex-col items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-50 text-primary-500">
                  <BiLockAlt />
                </div>

                <div className="text-center">
                  <p className="text-sm font-semibold text-neutral-500">
                    Password
                  </p>
                  <p className="text-xs text-neutral-300">
                    reset login credentials
                  </p>
                </div>
              </div>

              {/* Action Button */}
              <SubButton
                text="Change"
                type="button"
                color="bg-primary-500"
                padding="px-6"
                disabled={!isEditing}
                onClick={() => setShowPasswordModal(true)}
              />
            </div>
          </div>
        )}
      </div>

      {showPasswordModal && (
        <UpdatePassword
          onClose={() => setShowPasswordModal(false)}
          userId={staff?.userId}
        />
      )}
    </div>
  );
}
